import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/api/client";
import { useAuth } from "@/auth/AuthContext";
import { toast } from "sonner";
import { FileText, Plus, History, Trash2, Loader2 } from "lucide-react";
import { BriefForm } from "@/components/commercial-docs/BriefForm";
import { DocumentEditor } from "@/components/commercial-docs/DocumentEditor";
import { formatMoney } from "@/lib/money";

export default function CommercialDocs() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const canEdit = ["founder", "partner", "pm"].includes(user?.role);
  const canBulkDelete = ["founder", "partner"].includes(user?.role);
  const deepLinkQuoteId = searchParams.get("quoteId");
  const deepLinkDocId = searchParams.get("docId");
  const [view, setView] = useState(deepLinkQuoteId ? "brief" : "list"); // list | brief | editor
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeDoc, setActiveDoc] = useState(null);
  const [clientContacts, setClientContacts] = useState([]);
  const [showLegacy, setShowLegacy] = useState(false);
  const [legacySows, setLegacySows] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [deleting, setDeleting] = useState(false);

  const refreshList = () => api.get("/v1/commercial-docs/documents").then((r) => setDocuments(r.data.items || [])).catch(() => {});

  useEffect(() => {
    setLoading(true);
    refreshList().finally(() => setLoading(false));
  }, []);

  const openDoc = async (doc) => {
    setActiveDoc(doc);
    if (doc.client_id) {
      try {
        const r = await api.get(`/v1/clients/${doc.client_id}`);
        setClientContacts(r.data.contacts || []);
      } catch { setClientContacts([]); }
    }
    setView("editor");
  };

  // H (2026-02): deep-linking straight into a specific SOW/Document from elsewhere (e.g. the
  // Project Dashboard's "Documents" section) via ?docId=... — mirrors the existing ?quoteId=
  // brief-prefill deep link pattern.
  useEffect(() => {
    if (!deepLinkDocId) return;
    api.get(`/v1/commercial-docs/documents/${deepLinkDocId}`).then((r) => { setSearchParams({}); openDoc(r.data); }).catch(() => setSearchParams({}));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deepLinkDocId]);

  const backToList = () => {
    setSearchParams({});
    setView("list");
  };

  const loadLegacy = () => {
    if (showLegacy) { setShowLegacy(false); return; }
    api.get("/v1/sow/").then((r) => setLegacySows(r.data.items || [])).catch(() => setLegacySows([]));
    setShowLegacy(true);
  };

  const toggleSelect = (id) => setSelected((prev) => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const bulkDelete = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Delete ${selected.size} document(s)? This cannot be undone.`)) return;
    setDeleting(true);
    try {
      await api.post("/v1/commercial-docs/documents/bulk-delete", { ids: Array.from(selected) });
      toast.success(`${selected.size} document(s) deleted`);
      setSelected(new Set());
      refreshList();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.code || "Couldn't delete documents");
    } finally { setDeleting(false); }
  };

  if (view === "brief") {
    return (
      <BriefForm
        initial={deepLinkQuoteId ? { client_id: searchParams.get("clientId"), quote_id: deepLinkQuoteId, project_id: searchParams.get("projectId") } : null}
        onCancel={backToList}
        onGenerated={(doc) => { setSearchParams({}); refreshList(); openDoc(doc); }}
      />
    );
  }
  if (view === "editor" && activeDoc) {
    return (
      <DocumentEditor
        document={activeDoc}
        user={user}
        clientContacts={clientContacts}
        onBack={() => { setView("list"); refreshList(); }}
        onUpdated={(doc) => setActiveDoc(doc)}
      />
    );
  }

  return (
    <div className="p-8 space-y-6" data-testid="commercial-docs-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="font-editorial text-4xl tracking-tight flex items-center gap-2"><FileText className="h-7 w-7 text-[#B45309]" /> SOW / Documents</h1>
          <p className="text-sm text-[#57534E] mt-1">A deterministic Rule Engine selects clauses from your Project Brief — every clause is fully explainable. AI only fills in the wording variables.</p>
        </div>
        {canEdit && (
          <button onClick={() => setView("brief")} data-testid="new-document-btn"
            className="inline-flex items-center gap-1.5 rounded-md bg-[#FFC817] px-4 py-2 text-xs font-medium text-[#1C1C1E] hover:bg-[#E6B415]">
            <Plus className="h-3.5 w-3.5" /> New Document
          </button>
        )}
      </div>

      <div className="rounded-lg border border-[#E7E5E4] bg-[#FFFFFF] overflow-hidden">
        <header className="flex items-center justify-between border-b border-[#E7E5E4] px-4 py-3 text-xs uppercase tracking-wider text-[#A8A29E]">
          <span>Documents ({documents.length}) {selected.size > 0 && <span className="text-[#B45309]">· {selected.size} selected</span>}</span>
          {canBulkDelete && selected.size > 0 && (
            <button onClick={bulkDelete} disabled={deleting} data-testid="bulk-delete-documents-btn"
              className="inline-flex items-center gap-1.5 rounded-md border border-red-500/30 bg-red-500/5 px-2.5 py-1 text-[10px] text-red-600 hover:bg-red-500/10 disabled:opacity-50">
              {deleting ? <Loader2 className="h-3 w-3 animate-spin" /> : <Trash2 className="h-3 w-3" />} Delete Selected
            </button>
          )}
        </header>
        {loading ? (
          <div className="divide-y divide-[#E7E5E4]" data-testid="commercial-docs-skeleton">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="flex items-center justify-between px-4 py-3.5">
                <div className="animate-pulse h-3 w-40 rounded bg-[#F5F5F4]" />
                <div className="animate-pulse h-3 w-16 rounded bg-[#F5F5F4]" />
              </div>
            ))}
          </div>
        ) : documents.length === 0 ? (
          <div className="px-4 py-8 text-center text-xs text-[#A8A29E]" data-testid="documents-empty-list">No documents yet — link an approved Quote to a new Project Brief above.</div>
        ) : (
          <div className="divide-y divide-[#E7E5E4]" data-testid="documents-existing-list">
            {documents.map((d) => (
              <div key={d.id} data-testid={`document-list-item-${d.id}`} className="w-full flex items-center gap-3 px-4 py-3 text-sm hover:bg-[#F5F5F4]">
                {canBulkDelete && (
                  <input type="checkbox" data-testid={`document-select-${d.id}`} checked={selected.has(d.id)} onChange={() => toggleSelect(d.id)} className="h-4 w-4 accent-[#FFC817] shrink-0" />
                )}
                <button onClick={() => openDoc(d)} className="flex-1 flex items-center justify-between text-left min-w-0">
                  <div className="min-w-0">
                    <div className="text-[#1C1C1E]/90 truncate flex items-center gap-2">
                      {d.project_name || d.client_name}
                      {d.quote_outcome === "Won" && <span className="rounded-full bg-emerald-500/10 px-1.5 py-0.5 text-[9px] uppercase tracking-wider text-emerald-600">Won</span>}
                    </div>
                    <div className="text-[10px] text-[#A8A29E]">{d.client_name} · v{d.version || 0} · {d.clause_ids?.length || 0} clauses</div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <span className="font-mono text-xs text-[#57534E]">{formatMoney(d.total, d.currency)}</span>
                    <span className={`text-[10px] uppercase tracking-wider rounded-full px-2 py-0.5 ${d.status === "finalized" ? "bg-emerald-500/10 text-emerald-600 border border-emerald-500/30" : "bg-orange-500/10 text-orange-600 border border-orange-500/30"}`}>
                      {d.status === "finalized" ? "Finalised" : "Draft"}
                    </span>
                  </div>
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="rounded-lg border border-[#E7E5E4] bg-[#FFFFFF] overflow-hidden">
        <button onClick={loadLegacy} data-testid="legacy-sows-toggle" className="w-full flex items-center justify-between px-4 py-3 text-xs uppercase tracking-wider text-[#A8A29E] hover:text-[#B45309]">
          <span className="flex items-center gap-1.5"><History className="h-3.5 w-3.5" /> Legacy SOWs (read-only history, pre-Rule Engine)</span>
          <span>{showLegacy ? "Hide" : "Show"}</span>
        </button>
        {showLegacy && (
          <div className="divide-y divide-[#E7E5E4]" data-testid="legacy-sows-panel">
            {legacySows.length === 0 ? (
              <div className="px-4 py-6 text-center text-xs text-[#A8A29E]">No legacy SOWs</div>
            ) : (
              legacySows.map((s) => (
                <div key={s.id} data-testid={`legacy-sow-row-${s.id}`} className="px-4 py-2.5 text-sm flex items-center justify-between">
                  <div className="min-w-0">
                    <div className="text-[#1C1C1E]/80 truncate">{s.project_name || s.client_name}</div>
                    <div className="text-[10px] text-[#A8A29E]">{s.client_name} · v{s.version || 0}</div>
                  </div>
                  <span className="font-mono text-xs text-[#78716C]">{formatMoney(s.total, s.currency)}</span>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
}
